// =============================================================================
// Editing or deleting part of a repeating series — "only this one", "from
// this one on", "the whole series". Pure list transforms over the FULL event
// list (the detail view can show events from any calendar, and mapping the
// scoped list would silently drop the change); each returns the next list
// for Calendar.js to commit. `ctx` is what a new row needs to know:
// { sharedCalendarId, userId }.
//
// The form shows the series' *start* date. If the user changed that date,
// the occurrence they clicked moves by the same number of days; a multi-day
// event keeps its length.
// =============================================================================

import { format, addDays, parseISO } from 'date-fns';
import { expandEvents } from '../../utils/recurrence';
import { diffEventFields, dayShift, shiftDate, seriesWithoutOccurrence } from './utils';

const createdAudit = (userId) => [
  { id: `${new Date().toISOString()}-create`, type: 'created', userId, at: new Date().toISOString() }
];

// Apply `eventData` to the event (or whole series) with id `baseId`, with an
// audit entry naming the changed fields. `sharedCalendarId` keeps the event
// in its own calendar.
export const editWholeSeries = (allEvents, baseId, eventData, { sharedCalendarId, userId }) => {
  const now = new Date().toISOString();
  const me = userId;
  return allEvents.map((ev) => {
    if (ev.id !== baseId) return ev;
    const fields = diffEventFields(ev, eventData);
    const audit = Array.isArray(ev.audit) ? ev.audit : [];
    const newAudit =
      fields.length > 0
        ? [
            ...audit,
            {
              id: `${now}-${Math.random().toString(36).slice(2, 7)}`,
              type: 'updated',
              userId: me,
              at: now,
              fields
            }
          ]
        : audit;
    return {
      ...ev,
      ...eventData,
      audit: newAudit,
      sharedCalendarId: ev.sharedCalendarId || sharedCalendarId || null
    };
  });
};

// One occurrence becomes a standalone event; the series loses that day.
export const editOccurrenceOnly = (allEvents, base, occurrenceDate, eventData, { sharedCalendarId, userId }) => {
  const moved = shiftDate(occurrenceDate, dayShift(base.date, eventData.date));
  const span = dayShift(eventData.date, eventData.endDate || eventData.date);
  const standalone = {
    id: Date.now().toString(),
    ...eventData,
    date: moved,
    endDate: shiftDate(moved, span),
    recurrence: null,
    sharedCalendarId: base.sharedCalendarId || sharedCalendarId || null,
    audit: createdAudit(userId),
    comments: []
  };
  const remaining = seriesWithoutOccurrence(base, occurrenceDate);
  return [
    ...allEvents.map((e) => (e.id === base.id ? remaining : e)).filter(Boolean),
    standalone
  ];
};

// The series is cut the day before the occurrence; a new series carries the
// edit from there on.
export const editFromOccurrenceOn = (allEvents, base, occurrenceDate, eventData, ctx) => {
  // From the first occurrence on *is* the whole series.
  if (occurrenceDate <= base.date) {
    return editWholeSeries(allEvents, base.id, eventData, ctx);
  }
  const { sharedCalendarId, userId } = ctx;
  const moved = shiftDate(occurrenceDate, dayShift(base.date, eventData.date));
  const span = dayShift(eventData.date, eventData.endDate || eventData.date);
  const oldExceptions = base.recurrence.exceptions || [];
  // The old series now ends the day before; a `count` no longer applies
  // once it is cut by date.
  const { count, ...oldRule } = base.recurrence;
  const truncated = {
    ...base,
    recurrence: {
      ...oldRule,
      endType: 'until',
      until: shiftDate(occurrenceDate, -1),
      exceptions: oldExceptions.filter((d) => d < occurrenceDate)
    }
  };
  const rule = eventData.recurrence && eventData.recurrence.freq ? eventData.recurrence : base.recurrence;
  const { exceptions: ignored, ...cleanRule } = rule;
  const continued = {
    id: Date.now().toString(),
    ...eventData,
    date: moved,
    endDate: shiftDate(moved, span),
    recurrence: { ...cleanRule, exceptions: oldExceptions.filter((d) => d >= occurrenceDate) },
    sharedCalendarId: base.sharedCalendarId || sharedCalendarId || null,
    audit: createdAudit(userId),
    comments: []
  };
  return [
    ...allEvents.map((e) => (e.id === base.id ? truncated : e)),
    continued
  ];
};

// Remove one occurrence of `target`: an exception, or a later start when it
// is the first one (the whole series when nothing would be left).
export const deleteOccurrenceOnly = (allEvents, target, occurrenceDate) => {
  if (target.date === occurrenceDate) {
    // Deleting the very first occurrence: shift series start to next occurrence
    const nextRange = addDays(parseISO(occurrenceDate), 1);
    const future = expandEvents([target], nextRange, addDays(nextRange, 365 * 5));
    const nextOcc = future.find((o) => o.date > occurrenceDate);
    if (!nextOcc) {
      // No future occurrences -> delete entire series
      return allEvents.filter((e) => e.id !== target.id);
    }
    const newDate = nextOcc.date;
    const dur = parseISO(target.endDate || target.date).getTime() - parseISO(target.date).getTime();
    const newEndDate = format(new Date(parseISO(newDate).getTime() + dur), 'yyyy-MM-dd');
    const updated = {
      ...target,
      date: newDate,
      endDate: newEndDate
    };
    return allEvents.map((e) => (e.id === target.id ? updated : e));
  }
  const exceptions = [...(target.recurrence.exceptions || []), occurrenceDate];
  const updated = {
    ...target,
    recurrence: { ...target.recurrence, exceptions }
  };
  return allEvents.map((e) => (e.id === target.id ? updated : e));
};

// End `target` the day before the occurrence (the whole series when it is
// the first one).
export const deleteFromOccurrenceOn = (allEvents, target, occurrenceDate) => {
  if (target.date === occurrenceDate) {
    // Truncating from the very start = delete the whole series
    return allEvents.filter((e) => e.id !== target.id);
  }
  const newUntil = format(addDays(parseISO(occurrenceDate), -1), 'yyyy-MM-dd');
  const updated = {
    ...target,
    recurrence: {
      ...target.recurrence,
      endType: 'until',
      until: newUntil,
      count: undefined,
      custom: target.recurrence.custom || true
    }
  };
  return allEvents.map((e) => (e.id === target.id ? updated : e));
};
